import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { asyncScheduler } from 'rxjs';
import { UserService } from '@core/services';
// Direct path: ActiveProfileService is deliberately not re-exported from the barrel, since
// reaching it through the barrel closes an import cycle via SignalRService.
import { ActiveProfileService } from '@core/services/active-profile.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { PersonalizationService } from '@shared/services/personalization.service';
import { Profile } from '@/features/administration/user-profile/types/profile';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';

@Component({
  selector: 'cis-profile-switcher',
  imports: [ButtonModule, PopoverModule],
  template: `
    @if (availableProfiles()) {
      @if (availableProfiles().length > 1) {
        @if (activeProfile(); as profile) {
          <p-button
            text
            size="small"
            class="flex align-items-center gap-2"
            [style]="{
              padding: '0',
            }"
            (click)="toggle($event)">
            <span pButtonLabel>{{ profile.name }}</span>
            <i pButtonIcon [class]="'pi ' + chevronIcon()"></i>
          </p-button>
        }
        <p-popover #op>
          <ng-template #content>
            <div id="profile-switcher-content" class="w-full h-full flex flex-column gap-2">
              @for (profile of availableProfiles(); track profile.id) {
                <p-button
                  fluid
                  text
                  size="small"
                  styleClass="justify-content-start"
                  [disabled]="isSwitching()"
                  (click)="setProfile(profile)">
                  <span>{{ profile.name }}</span>
                </p-button>
              }
            </div>
          </ng-template>
        </p-popover>
      }
      @if (availableProfiles().length < 2) {
        @if (activeProfile(); as profile) {
          <div class="cursor-auto">
            <span>{{ profile.name }}</span>
          </div>
        }
      }
    }
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSwitcherComponent {
  private readonly userService = inject(UserService);
  private readonly personalizationService = inject(PersonalizationService);
  private readonly activeProfileService = inject(ActiveProfileService);

  op = viewChild<Popover>('op');
  isOpen = signal(false);

  /** Every profile the user holds. */
  availableProfiles = toSignal(this.userService.getCurrentUserProfiles(), {
    initialValue: [],
  });
  /** The one they are currently acting as. */
  activeProfile = toSignal(this.personalizationService.activeProfile$);

  isSwitching = this.userService.isSwitchingProfile;

  chevronIcon = computed(() => (this.isOpen() ? 'pi-chevron-up' : 'pi-chevron-down'));

  setProfile(profile: Profile) {
    // Hide the popover and let the overlay's own teardown run before the navigation storm.
    this.op()?.hide();
    this.isOpen.set(false);

    // Fire-and-forget on purpose: switchProfile re-navigates, and because
    // `shouldReuseRoute = () => false` that destroys THIS component - so nothing may touch
    // `this` after the await. The service owns all user feedback.
    asyncScheduler.schedule(() => {
      void this.activeProfileService.switchProfile(profile);
    });
  }

  toggle(event: Event) {
    event.stopPropagation();
    this.isOpen.update((prev) => !prev);
    this.op()?.toggle(event);
  }
}
